// @owners { team: patients-team }
import { useQueryClient } from '@tanstack/react-query';
import {
  type ScheduleablePrescriptionsProps,
  computeScheduleablePrescriptions,
} from '~shared/features/my-meds/selectors/getScheduleablePrescriptions';
import { useNextAvailableDatesByPrescriptionIdFromQueryCache } from '~shared/features/next-available-date/queries/useNextAvailableDatesByPrescriptionIdFromQueryCache';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';

const scheduleablePrescriptionsIdsEqual = (a: Prescription[], b: Prescription[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]?.id !== b[i]?.id) {
      return false;
    }
  }
  return true;
};

/**
 * Scheduleable prescriptions for the current Redux + checkout NAD (React Query) cache.
 * Subscribes to NAD cache updates via {@link useNextAvailableDatesByPrescriptionIdFromQueryCache} so UI stays aligned
 * with strategy A reads without relying on the Redux NAD bridge alone.
 */
export const useScheduleablePrescriptions = (props: ScheduleablePrescriptionsProps = {}): Prescription[] => {
  // Subscribe to RQ cache changes: when NAD data updates, this hook re-renders the component,
  // causing useSelectorShared below to re-run computeScheduleablePrescriptions with fresh queryClient data.
  useNextAvailableDatesByPrescriptionIdFromQueryCache();
  const queryClient = useQueryClient();

  return useSelectorShared(
    (state) => computeScheduleablePrescriptions(state, props, queryClient),
    scheduleablePrescriptionsIdsEqual,
  );
};

export const useScheduleableNonEssentialPrescriptions = (
  props: ScheduleablePrescriptionsProps = {},
): Prescription[] => {
  useNextAvailableDatesByPrescriptionIdFromQueryCache();
  const queryClient = useQueryClient();

  return useSelectorShared(
    (state) => computeScheduleablePrescriptions(state, props, queryClient).filter((rx) => !rx.is_add_on_otc),
    scheduleablePrescriptionsIdsEqual,
  );
};
